import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../store/portal/modalStore';
import { slideInFromLeftWeighted } from '../Animation';
import { getModalMessage } from '../../constants/modalMessages';
import { paperTextureModal } from '../paperTexture';
import TagStationTagRow from './TagStationTagRow';
import { TagStationActions } from './TagManagementTypes';

interface TagStationModalProps extends TagStationActions {
  setIsUserInput: (isUserInput: boolean) => void;
}

const TagStationModal: React.FC<TagStationModalProps> = ({
  tags,
  loadTags,
  loadKey,
  updateTag,
  deleteTag,
  getDeleteConfirmMessage,
  setIsUserInput,
}) => {
  const { isClosing } = useModalStore();
  const [dirtyByTagId, setDirtyByTagId] = useState<Record<number, true>>({});

  const handleRowDirtyChange = useCallback((tagId: number, dirty: boolean) => {
    setDirtyByTagId((prev) => {
      const next = { ...prev };
      if (dirty) next[tagId] = true;
      else delete next[tagId];
      return next;
    });
  }, []);

  useEffect(() => {
    setIsUserInput(Object.keys(dirtyByTagId).length > 0);
  }, [dirtyByTagId, setIsUserInput]);

  useEffect(() => {
    return () => setIsUserInput(false);
  }, [setIsUserInput]);

  useEffect(() => {
    if (!loadKey) return;
    void loadTags();
    // The adapter owns the request function; rerun only when its stable scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  return (
    <LeftModalFrame onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
      <TagStationComp>
        <TagStationTitle>{getModalMessage('TAG_STATION_TITLE')}</TagStationTitle>
        {tags.length === 0 && (
          <EmptyTagContainer>{getModalMessage('TAG_STATION_EMPTY')}</EmptyTagContainer>
        )}
        {tags.length > 0 && (
          <TagListScroll>
            {tags.map((tag) => (
              <TagStationTagRow
                key={tag.id}
                tag={tag}
                updateTag={updateTag}
                deleteTag={deleteTag}
                getDeleteConfirmMessage={getDeleteConfirmMessage}
                onDirtyChange={handleRowDirtyChange}
              />
            ))}
          </TagListScroll>
        )}
      </TagStationComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  left: 4vw;
  width: 20vw;
  min-width: 280px;
  max-width: 350px;
  height: fit-content;
  min-height: 20vh;
  max-height: 80vh;
  max-height: 80dvh;

  padding: 20px;
  box-sizing: border-box;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) => ($isClosing ? 'translateX(-100%)' : 'translateX(0)')};

  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const TagStationComp = styled.div`
  min-height: 0;
  max-height: calc(80vh - 40px);
  max-height: calc(80dvh - 40px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(10px, 3vh, 20px);
`;

const TagStationTitle = styled.div`
  margin-bottom: 0.5vh;
  flex-shrink: 0;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const TagListScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 4px;
  padding-right: 1px;
`;

const EmptyTagContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 100px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_05};
`;

export default TagStationModal;
